/** Feature areas for release notes. The slug is what the API stores and what `?area=` accepts. */
export const RELEASE_NOTE_AREAS = [
    { slug: 'home', label: 'Home' },
    { slug: 'feasibility', label: 'Feasibility' },
    { slug: 'builder', label: 'Builder' },
    { slug: 'surveys', label: 'Surveys' },
    { slug: 'dani', label: 'DANI' },
    { slug: 'crosstabs', label: 'Crosstabs' },
    { slug: 'connect', label: 'Connect' },
    { slug: 'board-reports', label: 'Board Reports' },
    { slug: 'slide-creator', label: 'Slide Creator' },
    { slug: 'insights', label: 'Insights' },
    { slug: 'credits-and-billing', label: 'Credits and Billing' },
    { slug: 'other', label: 'Other' },
] as const;

export type ReleaseNoteAreaSlug = (typeof RELEASE_NOTE_AREAS)[number]['slug'];

const AREA_SLUGS = new Set<string>(RELEASE_NOTE_AREAS.map((area) => area.slug));

export function isReleaseNoteAreaSlug(
    value: string | null | undefined,
): value is ReleaseNoteAreaSlug {
    return !!value && AREA_SLUGS.has(value);
}

export function releaseNoteAreaLabel(slug: string): string {
    return RELEASE_NOTE_AREAS.find((area) => area.slug === slug)?.label ?? slug;
}

/** "Chugach · 21 Oct 2026". A note written before release names existed has no label. */
export function formatReleaseLabel(
    releaseName: string | null | undefined,
    productionDate: string | null | undefined,
): string | null {
    const name = releaseName?.trim();
    if (!name) return null;
    if (!productionDate) return name;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(productionDate);
    if (!match) return name;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    const formatted = new Intl.DateTimeFormat('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date);
    return `${name} · ${formatted}`;
}

/**
 * A release-note image is one we signed. Anything else, including a pasted
 * image from another site, is not rendered.
 */
export function isOurReleaseNoteImageSrc(src: string | undefined): boolean {
    if (!src) return false;
    let url: URL;
    try {
        url = new URL(src);
    } catch {
        return false;
    }
    if (url.protocol !== 'https:') return false;
    const host = url.hostname;
    const amazon = host === 's3.amazonaws.com' || host.endsWith('.amazonaws.com');
    if (!amazon) return false;
    return decodeURIComponent(url.pathname).includes('/release-notes/');
}

/** Preview-only. The saved body keeps the rn-image key. */
export function previewReleaseNoteBody(body: string, imageUrls: Record<string, string>): string {
    return body.replace(
        /!\[([^\]]*)\]\(rn-image:(release-notes\/[0-9a-fA-F-]+)\)/g,
        (full, alt: string, key: string) => {
            const url = imageUrls[key];
            if (!url) return full;
            return `![${alt}](${url})`;
        },
    );
}

export function escapeReleaseNoteAlt(alt: string): string {
    return alt
        .replace(/[\[\]]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
